import {
  Box,
  BreakpointsOptions,
  Components,
  createTheme,
  css,
  CssBaseline,
  PaletteOptions,
  Theme,
  ThemeProvider,
} from '@mui/material';
import { ReactNode } from 'react';

import { lightTheme, sereneTheme } from '../../../../styles';

import { getTheme, getThemeEnum } from './shared/themeSelector/themeEnum';

const themeEnum = getThemeEnum();
const theme = getTheme(themeEnum);

// HA-489: text colors come from the palette. Forcing them here made primary text near-black in the dark theme.
const paletteOverrides: PaletteOptions = {
  ...theme?.palette,
};

const breakPointOverrides: BreakpointsOptions = {
  values: {
    xs: 0,
    sm: 640,
    md: 760,
    lg: 940,
    xl: 996,
  },
};

const componentOverrides: Components<Omit<Theme, 'components'>> = {
  MuiMenuItem: {
    styleOverrides: {
      root: {
        minWidth: 294,
        minHeight: 28,
        margin: 0,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: css({
        '&:not(.brand-icon)': {
          '&:hover': {
            border: 0,
            outline: 0,
          },
        },
        '&:focus': {
          border: 0,
          outline: 0,
        },
        '&:active': {
          border: 0,
          outline: 0,
        },
      }),
    },
  },
};

const themeOverride = createTheme({
  breakpoints: breakPointOverrides,
  components: componentOverrides,
  palette: paletteOverrides,
});

// HA-489: for pages drawn for a light background, whose colors are hardcoded (marketing, onboarding).
const keepLight = (outer: Theme) =>
  // No palette: rendered without an AppTheme, in a test.
  outer.palette && outer.palette.mode !== 'dark'
    ? outer
    : createTheme({
        ...lightTheme,
        breakpoints: { ...lightTheme.breakpoints, ...themeOverride.breakpoints },
        components: { ...lightTheme.components, ...themeOverride.components },
      });

// Sets the text color too: inherited text would otherwise keep the dark theme's white from CssBaseline.
export const LightSurface = ({ children }: { children: ReactNode }) => (
  <ThemeProvider theme={keepLight}>
    <Box sx={{ color: 'text.primary', display: 'contents' }}>{children}</Box>
  </ThemeProvider>
);

// @ts-expect-error -- props are intentionally untyped (implicit any)
export const AppTheme = ({ children, themeEnum }) => {
  return (
    <ThemeConfig themeEnum={themeEnum}>
      <ThemeProvider
        theme={(theme: Theme) =>
          createTheme({
            ...theme,
            breakpoints: {
              ...theme.breakpoints,
              ...themeOverride.breakpoints,
            },
            components: {
              ...theme.components,
              ...themeOverride.components,
            },
            palette: {
              ...theme.palette,
              ...themeOverride.palette,
            },
          })
        }
      >
        {children}
      </ThemeProvider>
    </ThemeConfig>
  );
};

// @ts-expect-error -- props are intentionally untyped (implicit any)
function ThemeConfig({ children, themeEnum }) {
  const theme = getTheme(themeEnum);

  return (
    <ThemeProvider theme={theme ?? sereneTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
